import { Box, IconButton } from '@chakra-ui/react';
import { keyframes } from '@emotion/react';

import { Bell } from '@enterprise/ui';

import type { NotificationBellProps } from './types/notification-bell.props';

import { NotificationPanel } from './components/notification-panel/notification-panel';
import { useNotificationBellConfiguration } from './hooks/notification-bell-configuration.hook';

// Slight scale-up "pop" on the bell on a new arrival (driven by `ring`). The wiggle itself
// uses FontAwesome's native `shake` on the icon (see `<Bell shake={ring} />` below) — a tuned
// rapid wiggle, rather than a single hand-rolled rotation.
const bellPop = keyframes`
  0% { transform: scale(1); }
  35% { transform: scale(1.14); }
  100% { transform: scale(1); }
`;

// Count badge pop when the unread total changes (driven by `bump`).
const badgeBump = keyframes`
  0% { transform: scale(1); }
  40% { transform: scale(1.3); }
  100% { transform: scale(1); }
`;

const renderBadge = (unreadCount: number, bump: boolean) => {
  if (unreadCount === 0) {
    return null;
  }

  const label = unreadCount > 99 ? '99+' : String(unreadCount);

  return (
    <Box
      alignItems="center"
      animation={bump ? `${badgeBump} 0.3s ease` : undefined}
      bg="red.500"
      border="2px solid"
      borderColor="white"
      borderRadius="full"
      color="white"
      data-bump={bump}
      display="inline-flex"
      fontSize="xs"
      fontWeight="bold"
      h="20px"
      justifyContent="center"
      lineHeight="1"
      minW="20px"
      position="absolute"
      px={1}
      right={-1.5}
      sx={{ fontVariantNumeric: 'tabular-nums' }}
      top={-1.5}>
      {label}
    </Box>
  );
};

export const NotificationBell = (_: NotificationBellProps) => {
  const {
    bump,
    containerRef,
    freshId,
    isOpen,
    nativePermission,
    notifications,
    onDelete,
    onDeleteAll,
    onOpen,
    onReadAll,
    onRequestNativePermission,
    onToggle,
    ring,
    unreadCount,
  } = useNotificationBellConfiguration();

  const renderPanel = () => {
    if (!isOpen) {
      return null;
    }

    return (
      <NotificationPanel
        freshId={freshId}
        nativePermission={nativePermission}
        notifications={notifications}
        onDelete={onDelete}
        onDeleteAll={onDeleteAll}
        onOpen={onOpen}
        onReadAll={onReadAll}
        onRequestNativePermission={onRequestNativePermission}
      />
    );
  };

  return (
    <Box
      display="inline-block"
      position="relative"
      ref={containerRef}>
      <Box position="relative">
        <IconButton
          animation={ring ? `${bellPop} 0.4s ease` : undefined}
          aria-label="Notifications"
          data-ring={ring}
          icon={
            <Bell
              shake={ring}
              size="xl"
            />
          }
          onClick={onToggle}
          variant="ghost"
        />
        {renderBadge(unreadCount, bump)}
      </Box>
      {renderPanel()}
    </Box>
  );
};
